import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import { getPost } from "@/lib/api";
import Newsletter from "@/components/Newsletter";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    getPost(slug).then(setPost).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="pt-40 pb-40 text-center" data-testid="post-not-found">
        <p className="text-zinc-400">Post not found.</p>
        <Link to="/blog" className="text-amber-500 mt-4 inline-block">Back to the journal</Link>
      </div>
    );
  }

  if (!post) return <div className="pt-40 pb-40 text-center text-zinc-600">Loading…</div>;

  return (
    <div data-testid="blog-post-page">
      <article>
        <div className="relative h-[50vh] min-h-[360px] overflow-hidden">
          <img src={post.cover_image} alt={post.title} className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-zinc-950/40" />
          <div className="relative z-10 h-full max-w-3xl mx-auto px-6 lg:px-8 flex flex-col justify-end pb-12">
            <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-amber-500 transition-colors mb-6">
              <ArrowLeft size={16} /> The Journal
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[10px] tracking-accent uppercase text-amber-500">{post.category}</span>
              <span className="text-xs text-zinc-400">{formatDate(post.published_at)}</span>
            </div>
            <h1 data-testid="blog-post-title" className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-tight">{post.title}</h1>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto px-6 lg:px-8 py-16"
        >
          {post.content.split("\n\n").map((para, i) => (
            <p key={i} className="text-lg text-zinc-300 leading-relaxed mb-6">{para}</p>
          ))}
        </motion.div>
      </article>

      <Newsletter />
    </div>
  );
}
